import ModalBackdrop from './ModalBackdrop'
import SkillTierRow from './profile/SkillTierRow'
import type { SkillTier } from '../skillTiers'
import { HOME_PICK_BASELINE_PCT } from '../skillMeterScale'
import { rulesSection } from '../features/rules/content'
import { useRulesScreen } from '../features/rules/rulesContext'
import SeeAllRulesRow from '../features/rules/SeeAllRulesRow'
import './SkillMeterInfoModal.css'

interface SkillMeterInfoModalProps {
  tiers: SkillTier[]
  tierIndex: number
  pct: number
  /** Pre-unlock: no tier is claimed yet, so the modal explains
   * what's coming instead of naming one — and the notes about the bar are
   * dropped, since there is no bar on the card in that state. */
  isCalibrating?: boolean
  onClose: () => void
}

function SkillMeterInfoModal({
  tiers,
  tierIndex,
  pct,
  isCalibrating = false,
  onClose,
}: SkillMeterInfoModalProps) {
  const tier = tiers[tierIndex]
  const { open: openRules } = useRulesScreen()

  return (
    <ModalBackdrop onClose={onClose}>
      <p className="skill-meter-modal__title">Intuition Rating & tiers</p>
      {/* This sentence lives in RULES_SECTIONS and is read from there. The modal
          keeps only what is its own — the gloss on "outcome", and everything below that depends on
          the viewer's own numbers. */}
      <p className="skill-meter-modal__description">
        {rulesSection('intuition').lede} Home win, draw or away win.
      </p>

      {/* Each row's glyph takes the tier's on-surface text colour ("-text"), not its fill tint,
          exactly as the card's own tier icon does it: the tints are ~16-18% alpha in dark mode
          (tuned as a wash behind text), so a 20px stroke icon in them reads washed out on the
          sunken row — most obviously while calibrating, where no row is highlighted to carry the
          colour. Not the ink ("-strong"): that token is graded near-black in
          light mode because it belongs on a tier tint fill, and these rows are neutral, so the
          icons read black rather than tiered. Dark mode is unchanged — "-text" aliases "-strong"
          there. */}
      <div className="skill-meter-modal__tiers">
        {tiers.map((t, i) => {
          const nextTier = tiers[i + 1]
          return (
            <SkillTierRow
              key={t.name}
              icon={t.icon}
              name={t.name}
              range={nextTier ? `${t.minPct}–${nextTier.minPct}%` : `${t.minPct}%+`}
              color={t.textColor}
              isCurrent={!isCalibrating && i === tierIndex}
            />
          )
        })}
      </div>

      {isCalibrating ? (
        <p className="skill-meter-modal__note">
          Three gameweeks in, we'll know what kind of predictor you are.
        </p>
      ) : (
        <>
          <p className="skill-meter-modal__note">
            You're at {Math.round(pct)}% correct outcome, in the {tier.name}{' '}
            tier.
          </p>
          {/* The commonest "why is my rating low" question, answered before it is asked. The
              rule itself is a bullet in RULES_SECTIONS; what belongs here is its consequence,
              which is the part that surprises people. */}
          <p className="skill-meter-modal__note">
            A fixture you never picked sits outside this figure on both sides of
            the sum, so a gameweek you missed leaves the number exactly where it
            was.
          </p>
          <p className="skill-meter-modal__note">
            The bar is zoomed into the 40–65% range instead of 0–100% — real
            skill differences between players cluster tightly in that band, so a
            wider scale would flatten everyone's progress into a sliver near the
            bottom.
          </p>
          {/* A fixed real-world figure, never a number computed from this
              season's data — see HOME_PICK_BASELINE_PCT. */}
          <p className="skill-meter-modal__note">
            The "Home picks" pin at {HOME_PICK_BASELINE_PCT}% marks what you'd
            roughly score by always predicting the home team to win — a
            reference point, not a target.
          </p>
        </>
      )}

      {/* The handoff instead of an ⓘ on the profile: the "Intuition Rating"
          label is already an info affordance, and a second one beside it is the failure mode this
          ticket names. Someone who opened this modal is exactly the person to offer the rest to.

          Same shared row as the chip sheet, with Close kept below it and spaced away, so the
          foot groups as [rules link] · · · [dismiss]. */}
      <SeeAllRulesRow
        onClick={() => {
          onClose()
          openRules('intuition')
        }}
      />

      <button className="skill-meter-modal__close" onClick={onClose}>
        Close
      </button>
    </ModalBackdrop>
  )
}

export default SkillMeterInfoModal
